const ACCOUNT_TYPE_OPTIONS = ['demo', 'live', 'wallet'];

function normalizeList(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (value && typeof value === 'object') {
    return Object.values(value);
  }
  return [];
}

function normalizeAccounts(payload) {
  const candidates = [payload?.accounts, payload?.items, payload?.data];
  for (const candidate of candidates) {
    const list = normalizeList(candidate);
    if (list.length === 0) {
      if (Array.isArray(candidate)) {
        return [];
      }
      continue;
    }

    const validRows = list.filter((item) => item && typeof item === 'object' && !Array.isArray(item));
    if (validRows.length > 0) {
      return validRows;
    }
  }
  return [];
}

function normalizePlatforms(payload) {
  const source = payload?.platforms ?? payload?.items ?? payload?.data ?? payload;
  return normalizeList(source)
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const value = String(item.value || '').trim();
      if (!value) {
        return null;
      }

      const label = String(item.label || value).trim() || value;
      return { value, label, capabilities: item.capabilities ?? {} };
    })
    .filter(Boolean);
}

function normalizeCurrencies(payload) {
  const sourceCandidates = [
    payload?.currencies,
    payload?.items,
    payload?.data,
    payload,
  ];

  const map = new Map();
  sourceCandidates.forEach((candidate) => {
    normalizeList(candidate).forEach((item) => {
      if (typeof item === 'string') {
        const value = item.trim().toUpperCase();
        if (value) {
          map.set(value, { value, label: value });
        }
        return;
      }

      if (!item || typeof item !== 'object') {
        return;
      }

      const value = String(item.code || item.currency || item.value || '').trim().toUpperCase();
      if (!value) {
        return;
      }

      const name = String(item.name || item.title || '').trim();
      map.set(value, { value, label: name ? `${value} - ${name}` : value });
    });
  });

  return Array.from(map.values()).sort((a, b) => a.value.localeCompare(b.value));
}

function normalizeTradingServers(payload) {
  return normalizeList(
    payload?.servers ?? payload?.items ?? payload?.data ?? payload,
  )
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      id: String(item.id || item.uid || '').trim(),
      name: String(item.name || '').trim(),
      platform: String(item.tradingPlatform || item.platform || '').trim(),
    }))
    .filter((item) => item.id);
}

export function mapTradingAccountItemToDto(item, index = 0) {
  return {
    id: String(item?.id || item?.uid || `trading-account-${index}`),
    tradingServerId: String(item?.tradingServerId || '').trim(),
    platform: String(item?.platform || '').trim(),
    name: String(item?.name || '').trim(),
    description: String(item?.description || '').trim(),
    type: String(item?.type || '').trim(),
    leverages: normalizeList(item?.leverages).map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0),
    currencies: normalizeList(item?.currencies).map((value) => String(value || '').trim().toUpperCase()).filter(Boolean),
    minimumDeposit: item?.minimumDeposit === null || item?.minimumDeposit === undefined || item?.minimumDeposit === '' ? null : Number(item.minimumDeposit),
    group: String(item?.group || '').trim(),
    platformUrl: String(item?.platformUrl || '').trim(),
    stopOutPercent: String(item?.stopOutPercent ?? '50.00'),
  };
}

export function mapTradingAccountsResponseToDto(payload) {
  return normalizeAccounts(payload).map((item, index) => mapTradingAccountItemToDto(item, index));
}

export function mapPlatformsResponseToDto(payload) {
  return normalizePlatforms(payload);
}

export function mapCurrenciesResponseToDto(payload) {
  return normalizeCurrencies(payload);
}

export function mapTradingServersResponseToDto(payload) {
  return normalizeTradingServers(payload);
}

export function parseLeverages(values) {
  const parts = normalizeList(values).map((value) => String(value || '').trim()).filter(Boolean);
  const unique = [];
  parts.forEach((part) => {
    const number = Number(part);
    if (!Number.isFinite(number) || number <= 0) {
      return;
    }
    if (!unique.includes(number)) {
      unique.push(number);
    }
  });

  return unique;
}

export function buildTradingAccountPayloadFromForm(form) {
  const capabilities = form?.capabilities ?? {};
  const leverages = parseLeverages(form?.leverages);
  const minimumDepositValue = String(form?.minimumDeposit || '').trim();
  const minimumDeposit = minimumDepositValue === '' ? null : Number(minimumDepositValue);

  return {
    ...(capabilities.supportsStopOut ? { stopOutPercent: String(form?.stopOutPercent ?? '50.00').trim() } : {}),
    platform: String(form?.platform || '').trim(),
    tradingServerId: String(form?.tradingServerId || '').trim(),
    name: String(form?.name || '').trim(),
    group: String(form?.group || '').trim(),
    platformUrl: String(form?.platformUrl || '').trim() || null,
    description: String(form?.description || '').trim(),
    type: String(form?.type || '').trim(),
    leverages,
    currencies: normalizeList(form?.currencies).map((value) => String(value || '').trim().toUpperCase()).filter(Boolean),
    minimumDeposit: minimumDeposit === null || Number.isFinite(minimumDeposit) ? minimumDeposit : null,
    minimumDepositRaw: minimumDepositValue,
  };
}

export function validateTradingAccountPayload(payload, capabilities = {}) {
  const nextErrors = {};
  if (!payload.platform) {
    nextErrors.platform = 'This field is required.';
  }
  if (!payload.tradingServerId) {
    nextErrors.tradingServerId = 'This field is required.';
  }
  if (!payload.name) {
    nextErrors.name = 'This field is required.';
  }
  if (!payload.description) {
    nextErrors.description = 'This field is required.';
  }
  if (!payload.type || !ACCOUNT_TYPE_OPTIONS.includes(payload.type)) {
    nextErrors.type = 'This field is required.';
  }
  if (payload.leverages.length === 0) {
    nextErrors.leverages = 'Please provide at least one leverage.';
  }
  if (payload.currencies.length === 0) {
    nextErrors.currencies = 'Please select at least one currency.';
  }
  if (payload.minimumDepositRaw !== '' && !Number.isFinite(payload.minimumDeposit)) {
    nextErrors.minimumDeposit = 'Invalid number.';
  }

  if (payload.platformUrl) {
    try {
      const url = new URL(payload.platformUrl);
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) throw new Error();
    } catch { nextErrors.platformUrl = 'Enter an HTTP(S) terminal URL without embedded credentials.'; }
  }
  if (capabilities.allowedAccountTypes?.length && !capabilities.allowedAccountTypes.includes(payload.type)) nextErrors.type = `Supported account types: ${capabilities.allowedAccountTypes.join(', ')}.`;
  if (capabilities.allowedCurrencies?.length && payload.currencies.some(value => !capabilities.allowedCurrencies.includes(value))) nextErrors.currencies = `Supported currencies: ${capabilities.allowedCurrencies.join(', ')}.`;
  if (payload.leverages.some(value => !Number.isInteger(value)
    || (capabilities.minLeverage != null && value < capabilities.minLeverage)
    || (capabilities.maxLeverage != null && value > capabilities.maxLeverage))) nextErrors.leverages = 'Enter whole-number leverage within the platform limits.';
  if (capabilities.supportsStopOut) {
    const stopOut = String(payload.stopOutPercent ?? '');
    if (!/^\d+(\.\d{1,2})?$/.test(stopOut) || Number(stopOut) <= 0 || Number(stopOut) > 100) nextErrors.stopOutPercent = 'Enter a percentage greater than 0 and at most 100 (up to two decimals).';
  }
  return nextErrors;
}

export function mapTradingAccountFieldErrors(requestError) {
  const nextFieldErrors = {};
  const details = Array.isArray(requestError?.errors) ? requestError.errors : [];

  details.forEach((item) => {
    const parameter = String(item?.parameter || '').trim();
    if (['platform', 'tradingServerId', 'name', 'group', 'platformUrl', 'description', 'type', 'leverages', 'currencies', 'minimumDeposit', 'stopOutPercent'].includes(parameter)) {
      nextFieldErrors[parameter] = String(item?.message || 'Invalid value').trim() || 'Invalid value';
    }
  });

  return nextFieldErrors;
}

export { ACCOUNT_TYPE_OPTIONS };
